import { useEffect, useRef } from "react";

const PARTICLE_DENSITY = 3000;
const MAX_PARTICLES = 340;
const MIN_PARTICLES = 170;
const CONNECTION_DIST = 88;
const CURSOR_RADIUS = 280;
const REPULSE_POWER = 1.55;
const MAX_PARTICLE_SPEED = 9.5;

const lerp = (a, b, t) => a + (b - a) * t;

const HeroCursorField = () => {
  const canvasRef = useRef(null);
  const spotRef = useRef(null);
  const ringRef = useRef(null);
  const trailRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const hero = canvas?.closest(".hero-v2");
    if (!canvas || !hero) return undefined;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reducedMotion) return undefined;

    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let particles = [];
    let rafId = null;

    const mouse = { x: -9999, y: -9999, active: false, vx: 0, vy: 0 };
    const spot = { x: 0, y: 0 };
    const ring = { x: 0, y: 0 };
    const trail = { x: 0, y: 0 };
    let lastX = 0;
    let lastY = 0;

    const resize = () => {
      const rect = hero.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedParticles();
    };

    const seedParticles = () => {
      const count = Math.min(
        MAX_PARTICLES,
        Math.max(MIN_PARTICLES, Math.floor((width * height) / PARTICLE_DENSITY))
      );

      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.42,
        vy: (Math.random() - 0.5) * 0.42,
        size: 0.55 + Math.random() * 1.5,
        alpha: 0.22 + Math.random() * 0.45,
        gold: Math.random() > 0.25,
      }));
    };

    const setPointer = (clientX, clientY) => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      const nx = clientX - rect.left;
      const ny = clientY - rect.top;
      mouse.vx = nx - lastX;
      mouse.vy = ny - lastY;
      lastX = nx;
      lastY = ny;
      mouse.x = nx;
      mouse.y = ny;
      mouse.active = true;
      hero.classList.add("hero--cursor-active");
    };

    const clearPointer = () => {
      mouse.active = false;
      hero.classList.remove("hero--cursor-active");
    };

    const placeEl = (el, x, y, scale = 1, opacity = 1) => {
      if (!el) return;
      el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
      el.style.opacity = String(opacity);
    };

    const updateCursorUi = () => {
      if (!mouse.active) {
        if (spotRef.current) spotRef.current.style.opacity = "0";
        if (ringRef.current) ringRef.current.style.opacity = "0";
        if (trailRef.current) trailRef.current.style.opacity = "0";
        return;
      }

      spot.x = lerp(spot.x, mouse.x, 0.42);
      spot.y = lerp(spot.y, mouse.y, 0.42);
      ring.x = lerp(ring.x, mouse.x, 0.16);
      ring.y = lerp(ring.y, mouse.y, 0.16);
      trail.x = lerp(trail.x, mouse.x, 0.07);
      trail.y = lerp(trail.y, mouse.y, 0.07);

      placeEl(spotRef.current, spot.x, spot.y, 1, 1);
      placeEl(ringRef.current, ring.x, ring.y, 1, 0.85);
      placeEl(
        trailRef.current,
        trail.x,
        trail.y,
        1 + Math.hypot(mouse.x - trail.x, mouse.y - trail.y) * 0.002,
        0.5
      );
    };

    const drawSoftLight = () => {
      const spotlight = ctx.createRadialGradient(
        mouse.x,
        mouse.y,
        0,
        mouse.x,
        mouse.y,
        Math.max(width, height) * 0.42
      );
      spotlight.addColorStop(0, "rgba(212, 175, 55, 0.1)");
      spotlight.addColorStop(0.22, "rgba(15, 118, 110, 0.04)");
      spotlight.addColorStop(0.55, "rgba(20, 18, 16, 0)");
      spotlight.addColorStop(1, "rgba(20, 18, 16, 0)");
      ctx.fillStyle = spotlight;
      ctx.fillRect(0, 0, width, height);

      const core = ctx.createRadialGradient(
        mouse.x,
        mouse.y,
        0,
        mouse.x,
        mouse.y,
        64
      );
      core.addColorStop(0, "rgba(253, 230, 138, 0.16)");
      core.addColorStop(0.45, "rgba(212, 175, 55, 0.06)");
      core.addColorStop(1, "rgba(212, 175, 55, 0)");
      ctx.fillStyle = core;
      ctx.fillRect(0, 0, width, height);
    };

    const applyCursorForces = (p) => {
      const dx = p.x - mouse.x;
      const dy = p.y - mouse.y;
      const dist = Math.hypot(dx, dy);
      if (dist >= CURSOR_RADIUS || dist < 0.01) return;

      const t = 1 - dist / CURSOR_RADIUS;
      const falloff = t * t * t;
      const push = falloff * REPULSE_POWER;

      p.vx += (dx / dist) * push;
      p.vy += (dy / dist) * push;

      const cursorSpeed = Math.hypot(mouse.vx, mouse.vy);
      if (cursorSpeed > 0.5) {
        const burst = falloff * Math.min(cursorSpeed * 0.055, 2.2);
        p.vx += (mouse.vx / cursorSpeed) * burst;
        p.vy += (mouse.vy / cursorSpeed) * burst;
      }

      const swirl = falloff * falloff * 0.18;
      p.vx += (-dy / dist) * swirl;
      p.vy += (dx / dist) * swirl;
    };

    const clampSpeed = (p) => {
      const speed = Math.hypot(p.vx, p.vy);
      if (speed > MAX_PARTICLE_SPEED) {
        p.vx = (p.vx / speed) * MAX_PARTICLE_SPEED;
        p.vy = (p.vy / speed) * MAX_PARTICLE_SPEED;
      }
    };

    const drawParticle = (p, boost) => {
      const r = p.size + boost * 0.9;
      const coreAlpha = Math.min(p.alpha + boost * 0.4, 0.95);
      const glowRadius = r * 3.1;

      const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowRadius);
      if (p.gold) {
        glow.addColorStop(0, `rgba(253, 230, 138, ${coreAlpha * 0.38})`);
        glow.addColorStop(0.35, `rgba(212, 175, 55, ${coreAlpha * 0.14})`);
        glow.addColorStop(1, "rgba(212, 175, 55, 0)");
      } else {
        glow.addColorStop(0, `rgba(245, 240, 232, ${coreAlpha * 0.28})`);
        glow.addColorStop(0.4, `rgba(245, 240, 232, ${coreAlpha * 0.08})`);
        glow.addColorStop(1, "rgba(245, 240, 232, 0)");
      }

      ctx.beginPath();
      ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fillStyle = p.gold
        ? `rgba(253, 230, 138, ${coreAlpha})`
        : `rgba(245, 240, 232, ${coreAlpha * 0.88})`;
      ctx.fill();
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      if (mouse.active) {
        drawSoftLight();
      }

      particles.forEach((p) => {
        if (mouse.active) {
          applyCursorForces(p);
        }

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 4) {
          p.x = 4;
          p.vx = Math.abs(p.vx) * 0.6;
        } else if (p.x > width - 4) {
          p.x = width - 4;
          p.vx = -Math.abs(p.vx) * 0.6;
        }
        if (p.y < 4) {
          p.y = 4;
          p.vy = Math.abs(p.vy) * 0.6;
        } else if (p.y > height - 4) {
          p.y = height - 4;
          p.vy = -Math.abs(p.vy) * 0.6;
        }

        p.vx *= 0.984;
        p.vy *= 0.984;
        clampSpeed(p);
      });

      if (mouse.active) {
        for (let i = 0; i < particles.length; i += 1) {
          for (let j = i + 1; j < particles.length; j += 1) {
            const a = particles[i];
            const b = particles[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const dist = Math.hypot(dx, dy);
            if (dist > CONNECTION_DIST) continue;

            const midX = (a.x + b.x) * 0.5;
            const midY = (a.y + b.y) * 0.5;
            const cursorDist = Math.hypot(midX - mouse.x, midY - mouse.y);
            const cursorBoost = Math.max(0, 1 - cursorDist / (CURSOR_RADIUS * 1.3));
            const alpha = (1 - dist / CONNECTION_DIST) * (0.06 + cursorBoost * 0.2);

            ctx.strokeStyle = `rgba(212, 175, 55, ${alpha})`;
            ctx.lineWidth = 0.6 + cursorBoost * 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        const cursorDist = mouse.active
          ? Math.hypot(p.x - mouse.x, p.y - mouse.y)
          : 9999;
        const boost = mouse.active
          ? Math.max(0, 1 - cursorDist / CURSOR_RADIUS) * 0.4
          : 0;

        drawParticle(p, boost);
      });

      updateCursorUi();
    };

    const tick = () => {
      draw();
      rafId = window.requestAnimationFrame(tick);
    };

    const onMouseMove = (e) => setPointer(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      const touch = e.touches[0];
      if (touch) setPointer(touch.clientX, touch.clientY);
    };

    resize();
    tick();

    window.addEventListener("resize", resize);
    hero.addEventListener("mousemove", onMouseMove, { passive: true });
    hero.addEventListener("mouseleave", clearPointer);
    hero.addEventListener("touchmove", onTouchMove, { passive: true });
    hero.addEventListener("touchend", clearPointer);

    return () => {
      window.removeEventListener("resize", resize);
      hero.removeEventListener("mousemove", onMouseMove);
      hero.removeEventListener("mouseleave", clearPointer);
      hero.removeEventListener("touchmove", onTouchMove);
      hero.removeEventListener("touchend", clearPointer);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      hero.classList.remove("hero--cursor-active");
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="hero-cursor-canvas"
        aria-hidden="true"
      />
      <div className="hero-cursor-ui" aria-hidden="true">
        <span ref={trailRef} className="hero-cursor-ring hero-cursor-ring--trail" />
        <span ref={ringRef} className="hero-cursor-ring" />
        <span ref={spotRef} className="hero-cursor-spot" />
      </div>
    </>
  );
};

export default HeroCursorField;
